import { useState } from "react";
import {
  loginAPI,
  createApprovedEmailPasswordAccount,
  loginWithGoogle,
  loginWithApple,
  loginWithMicrosoft,
} from "../../services/authService";
import { useAuth } from "../../contexts/AuthContext";
import { mapLoginErrorMessage } from "../../utils/loginAuthErrors";
import type { LoginResponse } from "../../types/user";
import type { StudioPolicyConsent } from "../../services/api/access";

export const useAuthActions = () => {
  const [error, setError] = useState<string | null>(null);
  const { loginAsGuestUser, logout, setError: setAuthError } = useAuth();

  const describeAuthError = (err: unknown): string => {
    const e = err as { code?: unknown; message?: unknown };
    const code = typeof e?.code === "string" ? e.code : null;
    const message = typeof e?.message === "string" ? e.message : String(err);
    return code ? `${message} (code=${code})` : message;
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setError(null);
      setAuthError(null);
      const response = await loginAPI(email, password);

      // 토큰 저장
      if (response.token) {
        localStorage.setItem("firebaseToken", response.token);
      }

      // onAuthStateChanged가 자동으로 처리하므로 여기서는 로그만 출력
    } catch (err) {
      console.error("이메일/비밀번호 로그인 에러:", err);
      const raw = describeAuthError(err);
      const errorMessage = mapLoginErrorMessage(
        `이메일/비밀번호 로그인 실패: ${raw}`,
      );
      setError(errorMessage);
      setAuthError(errorMessage);
      throw err;
    }
  };

  const createPasswordAccount = async (
    email: string,
    password: string,
    consent: StudioPolicyConsent,
  ): Promise<LoginResponse> => {
    try {
      setError(null);
      setAuthError(null);
      const response = await createApprovedEmailPasswordAccount(
        email,
        password,
        consent,
      );
      if (response.token) {
        localStorage.setItem("firebaseToken", response.token);
      }
      return response;
    } catch (err) {
      const raw = describeAuthError(err);
      const errorMessage = mapLoginErrorMessage(
        `Email/password account creation failed: ${raw}`,
      );
      setError(errorMessage);
      setAuthError(errorMessage);
      throw err;
    }
  };

  const loginGoogle = async (): Promise<void> => {
    try {
      setError(null);
      setAuthError(null);
      await loginWithGoogle();
    } catch (err) {
      console.error("Google 로그인 에러:", err);
      const raw = describeAuthError(err);
      const errorMessage = mapLoginErrorMessage(`Google 로그인 실패: ${raw}`);
      setError(errorMessage);
      setAuthError(errorMessage);
      throw err;
    }
  };

  const loginApple = async (): Promise<void> => {
    try {
      setError(null);
      setAuthError(null);
      await loginWithApple();
    } catch (err) {
      console.error("Apple 로그인 에러:", err);
      const raw = describeAuthError(err);
      const errorMessage = mapLoginErrorMessage(`Apple 로그인 실패: ${raw}`);
      setError(errorMessage);
      setAuthError(errorMessage);
      throw err;
    }
  };

  const loginMicrosoft = async (): Promise<void> => {
    try {
      setError(null);
      setAuthError(null);
      await loginWithMicrosoft();
    } catch (err) {
      console.error("Microsoft 로그인 에러:", err);
      const raw = describeAuthError(err);
      const errorMessage = mapLoginErrorMessage(
        `Microsoft 로그인 실패: ${raw}`,
      );
      setError(errorMessage);
      setAuthError(errorMessage);
      throw err;
    }
  };

  // Guest 로그인
  const loginGuest = async (): Promise<void> => {
    try {
      setError(null);
      setAuthError(null);
      await loginAsGuestUser("guest");
      // Guest 로그인 시에도 토큰 저장
      localStorage.setItem("firebaseToken", "guest-token-1234");
    } catch (err) {
      const errorMessage = "Guest sign-in failed. Please try again.";
      setError(errorMessage);
      setAuthError(errorMessage);
    }
  };

  return {
    login,
    createPasswordAccount,
    loginGoogle,
    loginApple,
    loginMicrosoft,
    loginGuest,
    logout,
    error,
  };
};
